import PaginationContext from '@jetshop/core/components/Pagination/PaginationContext';
import t from '@jetshop/intl';
import { PaginationBehaviour } from '@jetshop/ui/Pagination';
import { styled } from 'linaria/react';
import React, { useMemo, useEffect, useState } from 'react';
import { Pagination } from './Pagination/Pagination';
import CategoryHeaderLoadingState from './CategoryHeaderLoadingState';
import Filters from './Filters';
import ProductGrid from './ProductGrid';
import { theme } from '../Theme';
import CategoryLink from '@jetshop/ui/CategoryLink';
import { CategoryHeader } from './CategoryHeader';
import { StoreContext, useStore } from '../StoreHandler/StoreContext';
import StyledProductGridLarge from './ProductGridLarge';
import { ContentRenderer } from '@jetshop/ui/ContentRenderer';
import { contentEditorComponents } from '../ContentEditor/ContentEditorComponents';
import { useLipscore } from '../../hooks/useLipscore';

const CategoryWrapper = styled('div')`
  display: flex;
  width: 100%;

  .product-card {
    margin: 0;
    ${theme.below.lg} {
      padding: 0;
    }
  }
`;

const CategoriesLinksWrapper = styled('div')`
  display: flex;
  margin: auto;
  padding: 10px 20px;

  p {
    font-weight: 500;
  }

  .filter-buttonwrapper {
    z-index: 10;
    width: 100%;
    max-width: fit-content;
  }

  ${theme.below.lg} {
    margin: 0;
    padding: 0;
    margin-top: 4px;
    margin-bottom: 10px;
  }
`;

const CategoryNavigation = styled('div')`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CategoryNavigationItem = styled(CategoryLink)`
  padding: 10px 20px;
  background: #911830;
  color: #fff;
  margin-right: 10px;
  margin-top: 10px;
  font-weight: 300;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  min-width: fit-content;
  border-radius: 20px;
  display: flex;
  align-items: center;

  :hover {
    background: ${theme.colors.hoverRed};
  }

  :first-child {
    margin-left: 24px;
  }

  ${theme.below.lg} {
    width: 100%;
    min-width: fit-content;
    text-align: center;
    padding: 8px 20px;
    font-size: 11px;
    line-height: 11px;
  }
`;

const CatgoryInnerWrapper = styled('div')`
  width: 100%;

  ${theme.below.lg} {
    max-width: 100%;
  }
`;

const CategoryGridWrapper = styled('div')`
  margin: 0 20px;

  ${theme.below.lg} {
    /* margin: 0 16px; */
    margin: 0;
  }
`;

const TopContentRendererWrapper = styled('div')``;

const ContentRendererWrapper = styled('div')`
  width: 100%;
`;

// Separate CategoriesLinks into its own component for better memoization
const CategoriesLinks = React.memo(({ category }) => (
  <CategoryNavigation>
    {category?.subcategories?.map((subcategory) => (
      <CategoryNavigationItem key={subcategory.id} category={subcategory}>
        {subcategory?.name}
      </CategoryNavigationItem>
    ))}
  </CategoryNavigation>
));

const NoProductsToShowWrapper = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 20px;
`;

const NoProductsToShow = () => {
  return (
    <NoProductsToShowWrapper>
      <p>{t('no_products_to_show')}</p>
    </NoProductsToShowWrapper>
  );
};

// Add this new component for progressive loading
const ProgressiveRenderer = ({ children, placeholder, isReady }) => {
  return isReady ? children : placeholder || <div>Loading...</div>;
};

// Optimize the main component
const Category = ({ category, result }) => {
  // const { selectedStore } = useContext(StoreContext);
  const [initialRender, setInitialRender] = useState(true);
  const [urlPage, setUrlPage] = useState(null);

  const { noFilters } = useStore();

  // Initialize urlPage once on client-side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setUrlPage(new URLSearchParams(window.location.search).get('page'));
    }
  }, []);

  // Optimize Lipscore initialization
  useLipscore();

  // Memoize filtered items
  const topItems = useMemo(
    () =>
      category?.data?.items?.filter(
        (item) => item.type === 'CategoryImageLinksRow'
      ) || [],
    [category?.data?.items]
  );

  const bottomItems = useMemo(
    () =>
      category?.data?.items?.filter(
        (item) => item?.type !== 'CategoryImageLinksRow'
      ) || [],
    [category?.data?.items]
  );

  const products = category?.products?.result || [];

  // Critical first render optimization
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Force initial render without waiting for data
    setInitialRender(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !products?.length || result?.loading)
      return;

    // Defer non-critical initialization
    const lipscoreTimer = setTimeout(() => {
      if (window?.lipscore) window?.lipscore?.initWidgets();
    }, 2000); // Delay by 2 seconds to prioritize rendering

    return () => clearTimeout(lipscoreTimer);
  }, [products, result?.loading]);
  const headerContent = useMemo(
    () => category?.content?.split('<hr />')[0],
    [category?.content]
  );
  const categoryPath = category?.isDynamic ? null : result?.data?.route;
  const totalResults = category?.products?.totalResults;

  // Memoize common props passed to components
  const productGridProps = useMemo(
    () => ({
      products,
      listName: category?.name,
      categoryPath,
      loading: result?.loading,
      imageSizes: [1 / 2, 1 / 2, 1 / 3, 1 / 4],
    }),
    [products, category?.name, categoryPath, result?.loading]
  );

  // Add this to your component to identify what's causing the delay
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Create more detailed performance markers
    performance?.mark('category-component-mounted');
    return;
    // Create a custom performance timeline to track hydration and rendering
    // const observer = new PerformanceObserver((list) => {
    //   const entries = list.getEntries();
    // });

    // if (PerformanceObserver.supportedEntryTypes.includes('measure')) {
    //   observer.observe({ entryTypes: ['measure'] });
    // }

    // // Track when the DOM is fully interactive
    // window.addEventListener('DOMContentLoaded', () => {
    //   performance.mark('dom-content-loaded');
    //   performance.measure(
    //     'mount-to-interactive',
    //     'category-component-mounted',
    //     'dom-content-loaded'
    //   );
    // });

    // // Add more granular tracking
    // setTimeout(() => {
    //   if (document.readyState === 'complete') {
    //     performance.mark('after-1000ms');

    //     // Check what resources are still loading
    //     const resources = performance
    //       .getEntriesByType('resource')
    //       .filter((r) => r.responseEnd > performance.now() - 1000);
    //   }
    // }, 1000);

    // return () => {
    //   if (observer) observer.disconnect();
    // };
  }, []);

  // Early rendering path for category
  if (!category) {
    return <CategoryHeaderLoadingState />;
  }

  // Split your UI into critical and non-critical parts
  const criticalContent = (
    <>
      <CategoryHeader
        parents={result?.data?.route?.parents}
        breadcrumbText={category?.breadcrumbText}
        title={category?.name}
        content={headerContent}
      />
    </>
  );

  // Non-critical content that can wait for API calls
  const nonCriticalContent = (
    <>
      {topItems.length > 0 && (
        <TopContentRendererWrapper>
          <ContentRenderer
            items={topItems}
            components={contentEditorComponents}
          />
        </TopContentRendererWrapper>
      )}

      <CategoriesLinksWrapper>
        <div className="filter-buttonwrapper">
          <Filters
            filters={category?.products?.filters || []}
            sortOrders={category?.products?.sortOrders || []}
            result={result}
            totalProducts={totalResults}
          />
        </div>
        {category?.subcategories?.length > 0 && (
          <CategoriesLinks category={category} />
        )}
      </CategoriesLinksWrapper>

      <CategoryWrapper>
        <CatgoryInnerWrapper>
          <CategoryGridWrapper>
            <PaginationContext.Consumer>
              {(paginationProps) => {
                const isFirstPage = paginationProps?.currentPage === 1;
                const isNoFilters = noFilters?.get();

                if (isNoFilters) {
                  return <NoProductsToShow />;
                }

                return (
                  <>
                    {isFirstPage ? (
                      <StyledProductGridLarge
                        {...productGridProps}
                        items={category?.data?.items}
                      />
                    ) : (
                      <ProductGrid {...productGridProps} />
                    )}

                    <PaginationBehaviour
                      {...paginationProps}
                      total={totalResults}
                    >
                      {(props) => <Pagination {...props} />}
                    </PaginationBehaviour>
                  </>
                );
              }}
            </PaginationContext.Consumer>
          </CategoryGridWrapper>
        </CatgoryInnerWrapper>
      </CategoryWrapper>

      {bottomItems.length > 0 && (
        <ContentRendererWrapper>
          <ContentRenderer
            items={bottomItems}
            components={contentEditorComponents}
          />
        </ContentRendererWrapper>
      )}
    </>
  );

  // Return a progressively rendered UI
  return (
    <>
      {/* Always render critical content immediately */}
      {criticalContent}

      {/* Render non-critical content progressively */}
      <ProgressiveRenderer
        isReady={!initialRender || !productGridProps?.loading}
        placeholder={
          <div style={{ minHeight: '400px' }}>Loading products...</div>
        }
      >
        {nonCriticalContent}
      </ProgressiveRenderer>
    </>
  );
};

export default React.memo(Category);
